<template>
  <div class="quill-editor-wrapper" :class="{ 'readonly': readOnly, 'dark': systemStore.isDark }">
    <div v-if="!readOnly && toolbar !== 'none'" ref="toolbarRef" class="quill-toolbar-container">
      <slot name="toolbar">
        <div class="ql-toolbar-default">
          <span class="ql-formats">
            <select class="ql-header">
              <option value="1"></option>
              <option value="2"></option>
              <option value="3"></option>
              <option value="4"></option>
              <option value="5"></option>
              <option value="6"></option>
              <option selected></option>
            </select>
          </span>
          <span class="ql-formats">
            <button class="ql-bold"></button>
            <button class="ql-italic"></button>
            <button class="ql-underline"></button>
            <button class="ql-strike"></button>
          </span>
          <span class="ql-formats">
            <button class="ql-list" value="ordered"></button>
            <button class="ql-list" value="bullet"></button>
            <button class="ql-indent" value="-1"></button>
            <button class="ql-indent" value="+1"></button>
          </span>
          <span class="ql-formats">
            <select class="ql-align"></select>
            <button class="ql-link"></button>
            <button class="ql-image"></button>
            <button class="ql-video"></button>
          </span>
          <span class="ql-formats">
            <button class="ql-clean"></button>
          </span>
        </div>
      </slot>
    </div>
    <div ref="editorRef" class="quill-editor-content"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue'
import Quill from 'quill'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'
import { useSystemStore } from '@/stores'

interface Props {
  modelValue?: string
  readOnly?: boolean
  placeholder?: string
  theme?: 'snow' | 'bubble'
  toolbar?: string | any[] | 'none'
  modules?: any
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  readOnly: false,
  placeholder: '',
  theme: 'snow',
  toolbar: 'default'
})

const emit = defineEmits(['update:modelValue', 'change', 'blur', 'focus', 'ready'])

const systemStore = useSystemStore()
const editorRef = ref<HTMLElement | null>(null)
const toolbarRef = ref<HTMLElement | null>(null)
let quill: Quill | null = null

const initQuill = () => {
  if (!editorRef.value) return

  const modules: any = {
    toolbar: props.readOnly ? false : (props.toolbar === 'default' ? toolbarRef.value : props.toolbar),
    ...props.modules
  }

  // Quill 2.0 configurations
  quill = new Quill(editorRef.value, {
    theme: props.theme,
    modules,
    readOnly: props.readOnly,
    placeholder: props.placeholder,
  })

  if (props.modelValue) {
    quill.clipboard.dangerouslyPasteHTML(props.modelValue)
  }

  quill.on('text-change', () => {
    const html = quill!.root.innerHTML
    if (html === '<p><br></p>') {
      emit('update:modelValue', '')
    } else {
      emit('update:modelValue', html)
    }
    emit('change', html)
  })

  quill.on('selection-change', (range) => {
    if (range) {
      emit('focus')
    } else {
      emit('blur')
    }
  })

  emit('ready', quill)
}

onMounted(() => {
  nextTick(() => {
    initQuill()
  })
})

onUnmounted(() => {
  quill = null
})

watch(() => props.modelValue, (newVal) => {
  if (quill && newVal !== quill.root.innerHTML) {
    const selection = quill.getSelection()
    quill.clipboard.dangerouslyPasteHTML(newVal || '')
    if (selection) {
      quill.setSelection(selection)
    }
  }
})

watch(() => props.readOnly, (newVal) => {
  if (quill) {
    quill.enable(!newVal)
  }
})

// Methods for external use
const getQuill = () => quill
const setHTML = (html: string) => {
  if (quill) {
    quill.clipboard.dangerouslyPasteHTML(html || '')
  }
}

defineExpose({
  getQuill,
  setHTML
})
</script>

<style lang="scss">
.quill-editor-wrapper {
  display: flex;
  flex-direction: column;
  position: relative;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #fff;
  color: #000;

  &.dark {
    background: #1d1d1d;
    border-color: #444;

    .ql-toolbar {
      background-color: #2d2d2d;
      border-color: #444;
      color: #eee;

      .ql-stroke {
        stroke: #eee;
      }
      .ql-fill {
        fill: #eee;
      }
      .ql-picker {
        color: #eee;
      }
      .ql-picker-options {
        background-color: #2d2d2d;
        border-color: #444;
      }
    }

    .ql-container {
      background-color: transparent;
      border-color: #444;
      color: #eee;
    }

    .ql-editor.ql-blank::before {
      color: #888;
    }
  }

  &.readonly {
    border: none;
    background: transparent;

    .ql-container {
      border: none;
    }
  }

  .quill-editor-content {
    flex-grow: 1;
    min-height: 100px;
  }

  .ql-container.ql-snow {
    border: none;
  }

  .ql-toolbar.ql-snow {
    border: none;
    border-bottom: 1px solid #ccc;
  }
  
  &.dark .ql-toolbar.ql-snow {
    border-bottom: 1px solid #444;
  }
}
</style>
